import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ChatMessage from '../Onboarding/chat/ChatMessage';
import ProductChatComposer from './ProductChatComposer';
import ChatPromptMarquee from './ChatPromptMarquee';
import { CHAT_V2_EMPTY_HEADLINE, CHAT_V2_INPUT_PLACEHOLDER } from './chatV2Constants';
import useMobileKeyboardOffset from '../../hooks/useMobileKeyboardOffset';
import { MOBILE_PAGE_PAD_X } from '../../theme/mobileLayout';

export default function ChatEmptyStateV2({ onSend, disabled = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const keyboardOffset = useMobileKeyboardOffset();
  const keyboardOpen = keyboardOffset > 0;

  const handlePrompt = (prompt) => {
    const text = String(prompt || '').trim();
    if (!text || disabled) return;
    onSend?.(text);
  };

  const glowSx = {
    position: 'absolute',
    top: isMobile ? (keyboardOpen ? '66%' : '74%') : '46%',
    left: '50%',
    width: { xs: 360, md: 520 },
    height: { xs: 280, md: 280 },
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    background: isDark
      ? 'radial-gradient(ellipse at center, rgba(168,148,255,0.16) 0%, rgba(42,31,92,0.28) 42%, rgba(18,18,18,0) 72%)'
      : 'radial-gradient(ellipse at center, rgba(74,44,245,0.10) 0%, rgba(246,244,255,0.45) 42%, rgba(255,255,255,0) 72%)',
  };

  if (isMobile) {
    return (
      <Box
        data-testid="chat-empty-state-v2"
        data-keyboard-open={keyboardOpen ? 'true' : 'false'}
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'transparent',
          overflow: 'hidden',
        }}
      >
        <Box
          data-testid="chat-empty-state-mobile-scroll"
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            px: MOBILE_PAGE_PAD_X,
            pt: 1.5,
            pb: 1.5,
          }}
        >
          <Box
            data-testid="chat-empty-state-mobile-group"
            sx={{
              minHeight: '100%',
              width: '100%',
              maxWidth: 560,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: keyboardOpen ? 'flex-start' : 'center',
              alignItems: 'center',
              boxSizing: 'border-box',
              pt: keyboardOpen ? 3 : 1,
              pb: keyboardOpen ? 2 : 3,
              gap: keyboardOpen ? 2.5 : 3.5,
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              <ChatMessage role="app" variant="hero">
                {CHAT_V2_EMPTY_HEADLINE}
              </ChatMessage>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 460 }}>
              <ChatPromptMarquee onSelectPrompt={handlePrompt} disabled={disabled} />
            </Box>
          </Box>
        </Box>

        {/*
         * Input anchored at bottom.
         * - Shifts up by keyboard height so it stays just above the keyboard.
         * - Horizontal padding from design token keeps it off screen edges.
         * - Bottom padding uses safe-area-inset so it clears the home indicator.
         */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            flexShrink: 0,
            width: '100%',
            px: MOBILE_PAGE_PAD_X,
            pb: keyboardOpen
              ? `${keyboardOffset}px`
              : 'max(16px, env(safe-area-inset-bottom, 16px))',
            pt: 1.25,
            background: isDark
              ? `linear-gradient(180deg, transparent 0%, ${theme.palette.background.default} 28%)`
              : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.96) 28%)',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ProductChatComposer
            layout="hero"
            onSend={onSend}
            disabled={disabled}
            placeholder={CHAT_V2_INPUT_PLACEHOLDER}
            showSuggestions={false}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="chat-empty-state-v2"
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'transparent',
        overflow: 'hidden',
        px: { xs: 2, md: 3 },
        pt: { xs: 3, md: 2 },
        pb: { xs: 5, md: '10vh' },
      }}
    >
      <Box aria-hidden sx={glowSx} />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 920,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 2.5, md: 3.25 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 580,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2.5, md: 3.25 },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <ChatMessage role="app" variant="hero">
              {CHAT_V2_EMPTY_HEADLINE}
            </ChatMessage>
          </Box>

          <ProductChatComposer
            layout="hero"
            onSend={onSend}
            disabled={disabled}
            placeholder={CHAT_V2_INPUT_PLACEHOLDER}
            showSuggestions={false}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <ChatPromptMarquee onSelectPrompt={handlePrompt} disabled={disabled} />
        </Box>
      </Box>
    </Box>
  );
}
